import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { FaCheckCircle, FaDownload, FaHome } from 'react-icons/fa';
import { applicationAPI } from '../../services/api';
import toast from 'react-hot-toast';

const ApplicationSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [applicationId, setApplicationId] = useState(null);
    const [loading, setLoading] = useState(false);
    const applicationIdParam = searchParams.get('applicationId');

    useEffect(() => {
        if (applicationIdParam) {
            setApplicationId(applicationIdParam);
        }
    }, [applicationIdParam]);

    const handleDownloadApplication = async () => {
        if (!applicationId) {
            toast.error('Application ID not found');
            return;
        }

        // Check if user is logged in
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error('Please log in to download your application form');
            return;
        }

        setLoading(true);
        try {
            // Download PDF from backend
            const blob = await applicationAPI.downloadPDF(applicationId);
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Scholarship_Application_${applicationId}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast.success('Application form downloaded successfully!');
        } catch (err) {
            console.error('Download error:', err);
            toast.error(err.message || 'Failed to download application form');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
            <div className="max-w-2xl w-full">
                <div className="bg-white rounded-lg shadow-xl p-8 text-center">
                    {/* Success Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                            <FaCheckCircle className="text-5xl text-green-600" />
                        </div>
                    </div>

                    {/* Success Message */}
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">
                        Application Submitted Successfully!
                    </h1>
                    
                    <p className="text-lg text-gray-600 mb-6">
                        Thank you for submitting your scholarship application. Your application has been received and is under review.
                    </p>

                    {/* Application Details */}
                    {applicationId && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <p className="text-sm text-gray-600 mb-2">
                                <span className="font-semibold">Application ID:</span> {applicationId}
                            </p>
                            <p className="text-xs text-gray-500">
                                Please save this ID for future reference
                            </p>
                        </div>
                    )}

                    {/* Important Information */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
                        <h3 className="font-semibold text-yellow-900 mb-2">What happens next?</h3>
                        <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                            <li>Your application will be reviewed by our team</li>
                            <li>You will receive an email notification once your application status is updated</li>
                            <li>You can check your application status by logging into your account</li>
                        </ul>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        {applicationId && (
                            <button
                                onClick={handleDownloadApplication}
                                disabled={loading}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                            >
                                <FaDownload />
                                {loading ? 'Preparing...' : 'Download Application Form'}
                            </button>
                        )}
                        
                        <Link
                            to="/"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors font-medium"
                        >
                            <FaHome />
                            Return to Home
                        </Link>
                    </div>

                    {/* Login Prompt */}
                    <div className="mt-8 pt-6 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">
                            An account has been created for you. You can log in to check your application status.
                        </p>
                        <Link
                            to="/login"
                            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                        >
                            Go to Login →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicationSuccess;
